import {
  ApplicationsErrorCode,
  createApplicationsError,
} from '../Applications/ApplicationsError';
import { IApiConfigChecker, IApiHostConfig, classMetadataKey } from './common';
import { getMetadata } from 'reflect-metadata/no-conflict';

interface PathItem {
  metadata: IApiConfigChecker[];
  key: string | symbol;
}

const isMethodAllowed = (path: PathItem[], config: IApiHostConfig) => {
  const reversedPath = [...path].reverse();

  for (const step of reversedPath) {
    if (!step.metadata?.length) {
      continue;
    }
    return step.metadata.some((checker) => checker(config));
  }

  return true;
};

const getCurrentPath = (
  path: PathItem[] = [],
  classMetadata: IApiConfigChecker[],
): PathItem[] => {
  if (!classMetadata?.length) {
    return path;
  }

  return [...path, { metadata: classMetadata, key: classMetadataKey }];
};

export const buildLimitedPrivateApi = <T extends object>(
  api: T,
  config: IApiHostConfig,
  path: PathItem[] = [],
): T =>
  new Proxy(api, {
    get: (target, key) => {
      const metadata: IApiConfigChecker[] = getMetadata(
        'api-config',
        target,
        key,
      );

      const reflectValue = Reflect.get(target, key);
      const value =
        typeof reflectValue === 'function'
          ? reflectValue.bind(api)
          : reflectValue;

      if (!isMethodAllowed([...path, { metadata, key }], config)) {
        return undefined;
      }

      if (typeof value === 'object' || typeof value === 'function') {
        return buildLimitedPrivateApi(value as object, config, [
          ...getCurrentPath(
            path,
            getMetadata(
              'api-config',
              (value as object).constructor,
              classMetadataKey,
            ),
          ),
          { metadata, key },
        ]);
      }

      return value;
    },
    apply: (target, thisArg, argArray) => {
      const isAllowed = isMethodAllowed(path, config);

      if (!isAllowed) {
        const methodPath = path
          .map((item) => item.key)
          .filter((key) => key !== classMetadataKey)
          .join('.');

        throw createApplicationsError(
          ApplicationsErrorCode.ForbiddenPrivateApiCall,
        )
          .withMessage(
            "This method isn't allowed to be called in this type of application & environment",
          )
          .withMethod(methodPath)
          .withEnv(config.env)
          .withAppType(config.type);
      }

      return Reflect.apply(target as Function, thisArg, argArray);
    },
    set: (target, key, value) => {
      return Reflect.set(target, key, value);
    },
  });
